interface WidgetPostMessage<Payload = unknown> {
  widgetId: string
  type: string
  payload: Payload
}

type WidgetPostMessageCallback<Payload = unknown> = (event: MessageEvent<WidgetPostMessage<Payload>>) => void

const subscribeToPostMessages = <Payload = unknown>(
  widgetId: string,
  messageType: string,
  callback: WidgetPostMessageCallback<Payload>,
) => {
  const listener = (event: MessageEvent<WidgetPostMessage<Payload>>) => {
    if (event.data.widgetId === widgetId && event.data.type === messageType) {
      callback(event)
    }
  }
  window.addEventListener('message', listener)
  return () => window.removeEventListener('message', listener)
}

const sendPostMessageToParent = <Payload = unknown>(message: WidgetPostMessage<Payload>) => {
  window.parent.postMessage(message, '*')
}

const sendPostMessageToIframe = <Payload = unknown>(iframe: HTMLIFrameElement, message: WidgetPostMessage<Payload>) => {
  iframe.contentWindow?.postMessage(message, '*')
}

const replyToPostMessage = <Payload = unknown>(incomingEvent: MessageEvent, message: WidgetPostMessage<Payload>) => {
  incomingEvent.source?.postMessage(message, {
    targetOrigin: incomingEvent.origin,
  })
}

export const useWidgetPostMessages = <Payload>(widgetId: string, messageType: string) => {
  const subscribe = (callback: WidgetPostMessageCallback<Payload>) => {
    return subscribeToPostMessages(widgetId, messageType, callback)
  }
  const sendToParent = (payload: Payload) => {
    sendPostMessageToParent({ widgetId, type: messageType, payload })
  }
  const sendToIframe = (iframe: HTMLIFrameElement, payload: Payload) => {
    sendPostMessageToIframe(iframe, { widgetId, type: messageType, payload })
  }
  const reply = (event: MessageEvent, payload: Payload) => {
    replyToPostMessage(event, { widgetId, type: messageType, payload })
  }

  return {
    subscribe,
    sendToParent,
    sendToIframe,
    reply,
  }
}
